/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form, FormProps } from "@/components/form";
import { localNow } from "@/shared/local-now";
import { ConfirmDialog, useOpenModal } from "@/components/modal";
import { Grid2 as Grid, Stack, Typography } from "@mui/material";
import React from "react";
import { z } from "zod/v4";
import VentaGeneralInfoFields from "./venta-general-info-fields";
import VentaArticuloSearch from "./venta-articulo-search";
import VentaArticuloList from "./venta-articulo-list";
import { notifyError } from "@/shared/notification";
import useHasPermisos from "@/features/auth/hooks/useHasPermisos";
import { Permiso } from "@/shared/types/permiso";
import { useStore } from "@tanstack/react-form";
import { getSaldosDeposito } from "@/features/inventario/articulos/utils/validar-stock-items";
import { useDepositoContext } from "@/features/inventario/deposito-actual/contexts/deposito-context";

export const ventaFormSchema = z.object({
    vendedorId: z.string().nonempty("Este campo es requerido"),
    clienteId: z.string().nonempty("Este campo es requerido"),
    depositoId: z.string().nonempty("Este campo es requerido"),
    fecha: z.iso.datetime({ local: true, message: "Este campo es requerido" }).default(localNow()),
    credito: z.boolean(),
    presupuesto: z.boolean(),
    items: z.array(z.object({
        articuloId: z.string().nonempty("Este campo es requerido"),
        articuloNombre: z.string().optional(),
        articuloCodigo: z.string().optional(),
        articuloNumeroItem: z.string().nullish().optional(),
        cantidad: z.number().min(1, "La cantidad debe ser mayor que 0"),
        precioUnitario: z.number().gt(0, "El costo debe ser mayor que 0"),
        precioIndex: z.number(),
        stockDisponible: z.number().optional()
    })).min(1, "Debe haber por lo menos un ítem"),
    token: z.string().optional(),
    auto: z.boolean().default(true)
})

export function VentaForm({
    form,
    onAfterReset,
    draftRestoreSlot,
    ...props
}: FormProps<z.input<typeof ventaFormSchema>> & { onAfterReset?: () => void; draftRestoreSlot?: React.ReactNode }) {
    const openModal = useOpenModal();
    const hasAllowNegativeStock = useHasPermisos(Permiso.AllowNegativeStock);
    const { depositoData } = useDepositoContext();

    // Refrescar el stockDisponible de los ítems ya cargados cuando cambia el depósito;
    // las filas sin stock se marcan visualmente en la lista de artículos.
    const depositoId = useStore(form.store, s => (s.values as any).depositoId as string | undefined);

    React.useEffect(() => {
        if (!depositoId) return;
        const items = form.getFieldValue("items") ?? [];
        if (items.length <= 0) return;

        let cancelled = false;
        (async () => {
            const saldos = await getSaldosDeposito([...new Set(items.map(i => i.articuloId))], depositoId);
            if (cancelled) return;

            const currentItems = form.getFieldValue("items") ?? [];
            form.setFieldValue("items", currentItems.map(i =>
                saldos.has(i.articuloId) ? { ...i, stockDisponible: saldos.get(i.articuloId) } : i
            ));
        })();

        return () => { cancelled = true; };
    }, [depositoId]);

    // Completar el stockDisponible de ítems que no lo traen (precarga desde presupuesto, drafts viejos)
    // para que se verifiquen y marquen apenas haya un depósito seleccionado.
    const articulosSinStockDato = useStore(form.store, s => {
        const items = ((s.values as any).items ?? []) as { articuloId: string, stockDisponible?: number }[];
        return [...new Set(items.filter(i => i.stockDisponible == null).map(i => i.articuloId))].sort().join(",");
    });

    React.useEffect(() => {
        if (!depositoId || !articulosSinStockDato) return;

        let cancelled = false;
        (async () => {
            const saldos = await getSaldosDeposito(articulosSinStockDato.split(","), depositoId);
            if (cancelled || saldos.size <= 0) return;

            const currentItems = form.getFieldValue("items") ?? [];
            form.setFieldValue("items", currentItems.map(i =>
                i.stockDisponible == null && saldos.has(i.articuloId) ? { ...i, stockDisponible: saldos.get(i.articuloId) } : i
            ));
        })();

        return () => { cancelled = true; };
    }, [depositoId, articulosSinStockDato]);

    const handleReset = () => {
        const vendedorId = form.getFieldValue("vendedorId");
        form.reset();
        form.setFieldValue("vendedorId", vendedorId);
        if (depositoData?.id) form.setFieldValue("depositoId", depositoData.id);
        onAfterReset?.();
    }

    return <Form form={form} gap={3} {...props}>
        <VentaGeneralInfoFields />
        <Grid container columns={{ xs: 1, md: 3 }} spacing={5} flex={1}>
            <Grid component={Stack} size={1}>
                <VentaArticuloSearch onArticuloSelect={(v) => {
                    const presupuesto = form.getFieldValue("presupuesto");

                    if (!(v.precio > 0)) {
                        notifyError(`No se puede agregar "${v.articulo.nombre}": el precio debe ser mayor a 0.`);
                        return;
                    }

                    if (!presupuesto && !hasAllowNegativeStock && v.articulo.cantidad < v.cantidad) {
                        notifyError(`No hay suficiente stock del artículo ${v.articulo.nombre}. Stock disponible: ${v.articulo.cantidad}`);
                        return;
                    }

                    const items = form.getFieldValue("items");
                    const index = items.findIndex(i => i.articuloId === v.articulo.id)
                    if (index != -1) {
                        const previousValue = items[index];
                        // stockDisponible se actualiza con el valor recién consultado (más fresco).
                        const newValue = { ...previousValue, cantidad: previousValue.cantidad + v.cantidad, stockDisponible: v.articulo.cantidad }
                        form.replaceFieldValue("items", index, newValue);
                    } else {
                        form.pushFieldValue("items", {
                            articuloId: v.articulo.id,
                            articuloNombre: v.articulo.nombre,
                            articuloCodigo: v.articulo.codigo,
                            articuloNumeroItem: v.articulo.numeroItem,
                            cantidad: v.cantidad,
                            precioIndex: v.precioIndex,
                            precioUnitario: v.precio,
                            stockDisponible: v.articulo.cantidad
                        })
                    }
                }} />
            </Grid>
            <Grid component={Stack} size={2} gap={3}>
                <form.Field name="items" mode="array">
                    {field => <>
                        <VentaArticuloList
                            items={field.state.value}
                            remove={field.removeValue}
                        />
                        {field.state.meta.errors.length > 0 && (
                            <Typography color="error" variant="body2">
                                {field.state.meta.errors[0]?.message ?? field.state.meta.errors[0]}
                            </Typography>
                        )}
                    </>}
                </form.Field>
                <Stack direction={"row"} alignItems={"center"} gap={3}>
                    {draftRestoreSlot}
                    <Stack direction={"row"} justifyContent={"end"} gap={3} flex={1}>
                        <Form.Button onClick={() => openModal("confirmReset")}>Cancelar</Form.Button>
                        <Form.SubmitButton>Confirmar</Form.SubmitButton>
                    </Stack>
                </Stack>
            </Grid>
        </Grid>

        <ConfirmDialog code="confirmReset">
            <ConfirmDialog.Content
                title="Cancelar Venta"
                description="¿Estas seguro que quieres cancelar la venta actual?"
                onConfirm={handleReset}
            />
        </ConfirmDialog>
    </Form>
}